import React from 'react';
import Header from './HeaderHome';
import Footer from './FooterHome';

const HealthMedical = () => {
  // ── Key Programmes data ──
  const programmes = [
    {
      icon: "🏥",
      title: "Free Medical Camps",
      desc: "Organising health check-up camps in underserved areas, providing consultations, diagnostics, and basic treatments at no cost.",
    },
    {
      icon: "💊",
      title: "Medicine Distribution",
      desc: "Supplying essential medicines and healthcare supplies to economically disadvantaged patients who cannot afford treatment.",
    },
    {
      icon: "🩸",
      title: "Blood Donation Drives",
      desc: "Organizing regular blood donation camps to ensure availability of safe blood for emergency and routine medical needs.",
    },
    {
      icon: "🧘",
      title: "Mental Health Support",
      desc: "Offering counselling, stress management, and mental health awareness programmes to support emotional well-being.",
    },
    {
      icon: "🚑",
      title: "Emergency Medical Aid",
      desc: "Providing financial assistance, transport, and immediate medical help during emergencies and critical illnesses.",
    },
    {
      icon: "📋",
      title: "Health Awareness Campaigns",
      desc: "Conducting awareness sessions on hygiene, disease prevention, family planning, and nutrition to promote healthy communities.",
    },
  ];

  // ── Impact Stats ──
  const stats = [
    { number: "5,800+", label: "Patients Treated" },
    { number: "120+", label: "Medical Camps Conducted" },
    { number: "3,200+", label: "Medicines Distributed" },
    { number: "400+", label: "Surgeries Supported" },
  ];

  // ── How You Can Help ──
  const helpOptions = [
    { icon: "💊", title: "Donate Medicines", desc: "Contribute essential medicines and medical supplies." },
    { icon: "🩸", title: "Donate Blood", desc: "Participate in our blood donation drives." },
    { icon: "❤️", title: "Sponsor Treatment", desc: "Fund a patient's medical procedure or care." },
  ];

  return (
    <>
      <Header />

      {/* ========== HERO ========== */}
      <section className="health-hero relative text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="inline-block bg-primary/80 backdrop-blur-sm px-6 py-2 rounded-full text-sm font-medium mb-4">
              <i className="fas fa-heartbeat mr-2"></i> Our Service
            </div>
            <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-4">
              Health &amp; Medical Assistance
            </h1>
            <p className="text-xl md:text-2xl text-yellow-100/90 font-light">
              Bringing quality healthcare to the underserved—because health is the foundation of a dignified life
            </p>
            <div className="w-24 h-1 bg-yellow-300 mx-auto mt-6 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* ========== OVERVIEW ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800">
                Health &amp; Medical Assistance for All
              </h2>
              <div className="w-20 h-1 bg-primary mx-auto mt-3 rounded-full"></div>
            </div>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              Access to quality healthcare is a fundamental right, yet millions in India lack even basic medical
              services due to poverty, lack of awareness, and geographical barriers. At Venkatadri Seva Trust, our
              <strong>Health &amp; Medical Assistance</strong> programme is dedicated to bridging this gap by
              providing free and subsidised healthcare services to those in need.
            </p>
            <p className="text-gray-700 text-lg leading-relaxed">
              From organising health camps and distributing medicines to supporting life-saving surgeries and
              promoting mental well-being, we are committed to making healthcare accessible, affordable, and
              compassionate. We believe that a healthy community is a thriving community.
            </p>
          </div>
        </div>
      </section>

      {/* ========== VISION & MISSION ========== */}
      <section className="py-16 bg-primary">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="vision-card rounded-2xl p-8 text-white text-center">
              <div className="text-5xl mb-4">👁️</div>
              <h3 className="text-2xl font-bold mb-3">Our Vision</h3>
              <p className="text-yellow-50/90 text-lg leading-relaxed">
                A healthy India where every individual, regardless of economic status, has access to timely,
                quality medical care and preventive health services.
              </p>
            </div>
            <div className="vision-card rounded-2xl p-8 text-white text-center">
              <div className="text-5xl mb-4">🎯</div>
              <h3 className="text-2xl font-bold mb-3">Our Mission</h3>
              <p className="text-yellow-50/90 text-lg leading-relaxed">
                To provide holistic healthcare support—preventive, curative, and palliative—to the underserved,
                while promoting health awareness and building sustainable community health models.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== KEY PROGRAMMES ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white">Our Key Programmes</h2>
            <p className="text-yellow-100 mt-2 text-lg">
              Comprehensive healthcare solutions for the most vulnerable communities
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {programmes.map((programme, idx) => (
              <div
                key={idx}
                className="program-card bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-smooth text-center border-t-4 border-primary"
              >
                <div className="service-icon"><span>{programme.icon}</span></div>
                <h3 className="text-xl font-semibold text-gray-800">{programme.title}</h3>
                <p className="text-gray-600 mt-2 text-sm">{programme.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== IMPACT / STATS ========== */}
      {/* <section className="py-16 bg-primary">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white">Our Impact</h2>
            <div className="w-20 h-1 bg-yellow-300 mx-auto mt-3 rounded-full"></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto text-center text-white">
            {stats.map((stat, idx) => (
              <div key={idx}>
                <div className="text-4xl md:text-5xl font-bold text-yellow-300">{stat.number}</div>
                <p className="text-yellow-100/80 text-sm mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* ========== HOW YOU CAN HELP ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800">How You Can Help</h2>
              <div className="w-20 h-1 bg-primary mx-auto mt-3 rounded-full"></div>
              <p className="text-gray-600 mt-4 text-lg">
                Every contribution helps us save lives and restore health.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {helpOptions.map((option, idx) => (
                <div key={idx} className="text-center p-4 rounded-lg bg-gray-50 hover:bg-yellow-50 transition-smooth">
                  <div className="text-4xl text-primary mb-3">{option.icon}</div>
                  <h4 className="font-bold text-gray-800">{option.title}</h4>
                  <p className="text-gray-600 text-sm mt-1">{option.desc}</p>
                </div>
              ))}
            </div>

            <div className="text-center mt-8">
              <a
                href="/donate"
                className="bg-primary text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-dark transition-smooth inline-block"
              >
                <i className="fas fa-hand-holding-heart mr-2"></i> Support Healthcare
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ========== TESTIMONIAL ========== */}
      <section className="py-16 bg-primary">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center text-white">
            <div className="text-6xl mb-4">“</div>
            <p className="text-xl md:text-2xl font-light leading-relaxed italic">
              My son needed a heart surgery, and we couldn't afford it. Venkatadri Seva Trust stepped in and
              covered the entire cost. Today, my son is healthy and playing again. They gave us a second chance.
            </p>
            <div className="mt-6">
              <p className="font-semibold text-yellow-200">— Sita, 42</p>
              <p className="text-yellow-100/70 text-sm">Bengaluru</p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== JOIN OUR MISSION ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4 text-center text-white">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Join Our Mission</h2>
            <div className="w-20 h-1 bg-yellow-300 mx-auto mb-6 rounded-full"></div>
            <p className="text-xl text-yellow-100/90 italic mb-6">
              "Health is the greatest wealth—let’s ensure no one is left behind."
            </p>
            <p className="text-lg text-white/80 leading-relaxed mb-8">
              Your generous support—through donations, volunteering, or partnerships—helps us reach more patients,
              fund critical treatments, and build healthier communities across India.
            </p>
            <p className="text-xl text-yellow-100 font-light mb-8">
              Together, we can create a healthier future for everyone.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="/donate"
                className="bg-white text-primary px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-smooth"
              >
                <i className="fas fa-heart mr-2"></i> Donate Now
              </a>
              <a
                href="/volunteer"
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition-smooth"
              >
                <i className="fas fa-hand-peace mr-2"></i> Volunteer
              </a>

            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default HealthMedical;